import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Drawer, Progress, Segmented, Select, Skeleton, Table, Tag, type TableColumnsType } from 'antd';
import type { ManagementDashboardPerson, ManagementDashboardProject, ManagementDashboardQuery } from '@youkuai/contracts';
import { useMemo, useState } from 'react';

import { useAuth } from './auth.tsx';
import { Icon } from './icons.tsx';
import { exportManagementDashboard, getManagementDashboard, getManagementDashboardPerson } from './operations-v2-api.ts';

type View = 'PROJECTS' | 'PEOPLE';
type Period = 'WEEK' | 'MONTH' | 'ALL' | 'CUSTOM';

const PROJECT_STATUS = { PREPARING: '准备中', ACTIVE: '进行中', SUSPENDED: '已暂停', ARCHIVED: '已归档' } as const;
const DUTY_LABELS: Record<string, string> = { PROJECT_MANAGER: '项目管理', PACKAGE_MANAGER: '服务包负责',
  CONTENT_PLANNER: '内容策划', VIDEO_EDITOR: '视频剪辑', VIDEO_PRODUCTION: '视频制作',
  PUBLICATION_OPERATOR: '发布专员' };
const FACT_LABELS = { PLANNING: '策划', EDITING: '剪辑', PUBLICATION: '发布',
  MANAGEMENT_DELIVERY: '项目管理交付', UNCLASSIFIED: '未归类' } as const;
const ENGAGEMENT_LABELS = { EMPLOYEE: '内部员工', LONG_TERM_EXECUTOR: '外部合作' } as const;

function shanghaiToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit',
    day: '2-digit' }).format(new Date());
}
function rangeFor(period: Exclude<Period, 'CUSTOM'>): { from: string; until: string } {
  const today = shanghaiToday();
  if (period === 'ALL') return { from: '1900-01-01', until: today };
  const current = new Date(`${today}T00:00:00+08:00`);
  if (period === 'MONTH') return { from: `${today.slice(0, 7)}-01`, until: today };
  const day = current.getDay() || 7;
  current.setDate(current.getDate() - day + 1);
  return { from: new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit',
    day: '2-digit' }).format(current), until: today };
}
function dateLabel(from: string, until: string): string {
  return `${from.replaceAll('-', '.')} — ${until.replaceAll('-', '.')}`;
}
function periodLabel(period: Period, range: { from: string; until: string }): string {
  return period === 'ALL' ? '全量周期' : dateLabel(range.from, range.until);
}
function errorText(error: unknown): string {
  return error instanceof Error ? error.message : '管理看板读取失败';
}
function DutyTags({ duties }: { duties: string[] }) {
  return <span className="management-duty-tags">{duties.map((duty) =>
    <Tag key={duty}>{DUTY_LABELS[duty] ?? duty}</Tag>)}</span>;
}

export function ManagementDashboardPage() {
  const auth = useAuth();
  const session = auth.state.kind === 'ready' ? auth.state : null;
  const initial = rangeFor('MONTH');
  const [period, setPeriod] = useState<Period>('MONTH');
  const [range, setRange] = useState(initial);
  const [view, setView] = useState<View>('PROJECTS');
  const [managerId, setManagerId] = useState<string | null>(null);
  const [businessEntityId, setBusinessEntityId] = useState<string | null>(null);
  const [projectStatus, setProjectStatus] = useState<ManagementDashboardQuery['projectStatus']>('ACTIVE');
  const [duty, setDuty] = useState<ManagementDashboardQuery['duty']>(null);
  const [engagementTypes, setEngagementTypes] = useState<ManagementDashboardQuery['engagementTypes']>([]);
  const [personIds, setPersonIds] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<ManagementDashboardProject | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<ManagementDashboardPerson | null>(null);
  const [exporting, setExporting] = useState(false);
  const filter = useMemo<ManagementDashboardQuery>(() => ({ from: range.from, until: range.until, managerId,
    businessEntityId, projectStatus, duty, engagementTypes, personIds }),
  [range, managerId, businessEntityId, projectStatus, duty, engagementTypes, personIds]);
  const dashboard = useQuery({ queryKey: ['management-dashboard', session?.session.userId, filter],
    queryFn: () => getManagementDashboard(session!.token, filter), enabled: !!session, staleTime: 30_000 });
  const personDetail = useQuery({ queryKey: ['management-dashboard-person', session?.session.userId,
    selectedPerson?.personId, filter], queryFn: () => getManagementDashboardPerson(session!.token,
    selectedPerson!.personId, filter), enabled: !!session && !!selectedPerson, staleTime: 30_000 });
  const data = dashboard.data;
  const riskCount = data?.projects.filter((project) => project.atRisk).length ?? 0;
  const delivered = data?.projects.reduce((total, project) => total + project.periodDeliveredCount, 0) ?? 0;
  const backlog = data?.projects.reduce((total, project) => total + project.backlogCount, 0) ?? 0;
  const peopleOptions = (data?.peopleOptions ?? []).filter((person) => !engagementTypes.length ||
    engagementTypes.includes(person.engagementType));

  function changePeriod(next: Period) {
    setPeriod(next);
    if (next !== 'CUSTOM') setRange(rangeFor(next));
  }
  async function downloadExport() {
    if (!session) return;
    setExporting(true);
    try {
      const file = await exportManagementDashboard(session.token, filter);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a'); link.href = url;
      link.download = period === 'ALL' ? '管理看板-全量周期.csv' : `管理看板-${filter.from}-${filter.until}.csv`;
      link.click(); URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  }

  const projectColumns: TableColumnsType<ManagementDashboardProject> = [
    { title: '项目', dataIndex: 'projectName', key: 'projectName', width: 220,
      render: (_: unknown, row: ManagementDashboardProject) => <span className="management-project-name">
        <i>{row.projectName.slice(0, 1)}</i><span><b>{row.projectName}</b><small>{row.businessEntityName}</small></span></span> },
    { title: '负责人', dataIndex: 'managerName', key: 'managerName', width: 110 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (status: ManagementDashboardProject['status']) => <Tag color={status === 'ACTIVE' ? 'processing' : 'default'}>{PROJECT_STATUS[status]}</Tag> },
    { title: '包消化率', key: 'utilizationRate', width: 170,
      render: (_: unknown, row: ManagementDashboardProject) => <span className="management-progress-cell"><Progress
        percent={row.utilizationRate} size="small" strokeColor="#2563eb" /><small>{row.usedCapacity} / {row.totalCapacity}</small></span> },
    { title: '内容健康度', key: 'health', width: 190,
      render: (_: unknown, row: ManagementDashboardProject) => <span className="management-health-cell">
        <b>{row.completedCount}</b><small>完成</small><b>{row.inTransitCount}</b><small>在途</small>
        <b className={row.backlogCount >= 10 ? 'is-risk' : ''}>{row.backlogCount}</b><small>积压</small></span> },
    { title: '周期交付量', dataIndex: 'periodDeliveredCount', key: 'periodDeliveredCount', width: 110,
      render: (count: number) => <b>{count}</b> },
    { title: '内容交付完成率', key: 'contentDeliveryCompletionRate', width: 190,
      render: (_: unknown, row: ManagementDashboardProject) => <span className="management-progress-cell"><Progress
        percent={row.contentDeliveryCompletionRate} size="small"
        status={row.contentDeliveryCompletionRate < 50 ? 'exception' : 'normal'} />
        <small>{row.periodCohortDeliveredCount} / {row.periodCreatedCount} · 同期创建内容</small></span> },
    { title: '服务包履约进度', key: 'packageFulfillmentRate', width: 190,
      render: (_: unknown, row: ManagementDashboardProject) => <span className="management-progress-cell"><Progress
        percent={row.packageFulfillmentRate} size="small" strokeColor="#0f8a6a" />
        <small>{row.cumulativeDeliveredCount} / {row.totalCapacity} · 累计交付</small></span> },
    { title: '风险', key: 'risk', width: 88,
      render: (_: unknown, row: ManagementDashboardProject) => row.atRisk
        ? <Tag color="error"><Icon name="alert" size={12} /> 关注</Tag> : <Tag color="success">正常</Tag> },
  ];
  const peopleColumns: TableColumnsType<ManagementDashboardPerson> = [
    { title: '人员', dataIndex: 'displayName', key: 'displayName', fixed: 'left' as const, width: 120,
      render: (name: string) => <b>{name}</b> },
    { title: '人员类型', dataIndex: 'engagementType', key: 'engagementType', width: 100,
      render: (value: ManagementDashboardPerson['engagementType']) => <Tag
        color={value === 'EMPLOYEE' ? 'blue' : 'green'}>{ENGAGEMENT_LABELS[value]}</Tag> },
    { title: '职责', dataIndex: 'duties', key: 'duties', width: 210,
      render: (duties: string[]) => <DutyTags duties={duties} /> },
    { title: '策划', dataIndex: 'planningCount', key: 'planningCount', width: 72 },
    { title: '剪辑', dataIndex: 'editingCount', key: 'editingCount', width: 72 },
    { title: '发布', dataIndex: 'publicationCount', key: 'publicationCount', width: 72 },
    { title: '管理交付', dataIndex: 'managementDeliveryCount', key: 'managementDeliveryCount', width: 94 },
    { title: '推进项目', dataIndex: 'managedProjectCount', key: 'managedProjectCount', width: 94 },
    { title: '完成', dataIndex: 'completedCount', key: 'completedCount', width: 72 },
    { title: '已交付', dataIndex: 'deliveredCount', key: 'deliveredCount', width: 80 },
    { title: '在途', dataIndex: 'inTransitCount', key: 'inTransitCount', width: 72 },
    { title: '积压', dataIndex: 'backlogCount', key: 'backlogCount', width: 72,
      render: (count: number) => <b className={count >= 10 ? 'management-risk-number' : ''}>{count}</b> },
    { title: '未归类', dataIndex: 'unclassifiedCount', key: 'unclassifiedCount', width: 82 },
  ];

  return <main className="page-shell management-dashboard-page">
    <header className="management-dashboard-hero">
      <div className="management-hero-copy"><span className="management-hero-kicker"><i /> OPERATIONS CONTROL ROOM</span>
        <h1>管理看板</h1>
        <p>把项目推进、交付健康度与团队负荷放进同一张事实表</p>
        <span className="management-hero-period"><Icon name="calendar" size={14} />{periodLabel(period, range)}</span>
      </div>
      <div className="management-hero-owl" aria-hidden="true"><i className="management-owl-orbit" />
        <img src="/management-dashboard/owl-ops-hero.webp" alt="" width="512" height="512"
          decoding="async" fetchPriority="high" />
        <span><b>运营助手</b><small>数据已就绪</small></span>
      </div>
      <div className="management-view-switch"><small>查看视角</small><Segmented value={view} onChange={(value) => setView(value as View)}
        options={[{ label: '项目视角', value: 'PROJECTS' }, { label: '人效视角', value: 'PEOPLE' }]} />
        {data?.scope.canExport && view === 'PEOPLE' && <Button loading={exporting} onClick={() => void downloadExport()}
          icon={<Icon name="download" size={14} />}>导出人效</Button>}</div></header>
    <section className="management-filter-rail"><header><span><Icon name="filter" size={16} /></span><div><b>看板筛选</b>
      <small>调整范围后，指标与明细将同步更新</small></div></header><div className="management-filter-controls">
      <label><span>周期</span><Segmented size="small" value={period} onChange={(value) => changePeriod(value as Period)}
        options={[{ label: '本周', value: 'WEEK' }, { label: '本月', value: 'MONTH' },
          { label: '全量', value: 'ALL' }, { label: '自选', value: 'CUSTOM' }]} /></label>
      {period === 'CUSTOM' && <label><span>日期范围</span><span className="management-date-range"><input type="date"
        value={range.from} max={range.until} onChange={(event) => setRange((current) => ({ ...current, from: event.target.value }))} />
        <i>至</i><input type="date" value={range.until} min={range.from}
          onChange={(event) => setRange((current) => ({ ...current, until: event.target.value }))} /></span></label>}
      {data?.scope.canFilterManagers && <label><span>项目经理</span><Select allowClear value={managerId} placeholder="全部负责人"
        onChange={(value) => setManagerId(value ?? null)} options={data.managers.map((manager) =>
          ({ value: manager.personId, label: manager.displayName }))} /></label>}
      <label><span>业务实体</span><Select allowClear value={businessEntityId} placeholder="全部业务实体"
        onChange={(value) => setBusinessEntityId(value ?? null)} options={(data?.businessEntities ?? []).map((entity) =>
          ({ value: entity.businessEntityId, label: entity.name }))} /></label>
      <label><span>项目状态</span><Select allowClear value={projectStatus} placeholder="全部状态"
        onChange={(value) => setProjectStatus(value ?? null)} options={Object.entries(PROJECT_STATUS).map(([value, label]) => ({ value, label }))} /></label>
      {view === 'PEOPLE' && <label><span>职责</span><Select allowClear value={duty} placeholder="全部职责"
        onChange={(value) => setDuty(value ?? null)} options={Object.entries(DUTY_LABELS).map(([value, label]) => ({ value, label }))} /></label>}
      {view === 'PEOPLE' && <label><span>人员类型</span><Select mode="multiple" allowClear maxTagCount="responsive"
        value={engagementTypes} placeholder="全部类型" options={Object.entries(ENGAGEMENT_LABELS).map(([value, label]) => ({ value, label }))}
        onChange={(values) => { const next = values as ManagementDashboardQuery['engagementTypes']; setEngagementTypes(next);
          const allowed = new Set((data?.peopleOptions ?? []).filter((person) => !next.length ||
            next.includes(person.engagementType)).map((person) => person.personId));
          setPersonIds((current) => current.filter((personId) => allowed.has(personId))); }} /></label>}
      {view === 'PEOPLE' && <label className="management-people-filter"><span>展示人员</span><Select mode="multiple"
        allowClear showSearch optionFilterProp="label" maxTagCount="responsive" value={personIds} placeholder="全部人员（可多选）"
        onChange={setPersonIds} options={peopleOptions.map((person) => ({ value: person.personId,
          label: `${person.displayName} · ${ENGAGEMENT_LABELS[person.engagementType]}` }))} /></label>}</div>
    </section>
    {dashboard.isError && <Alert type="error" showIcon title="管理看板读取失败" description={errorText(dashboard.error)}
      action={<Button onClick={() => void dashboard.refetch()}>重试</Button>} />}
    {dashboard.isPending && <Skeleton active paragraph={{ rows: 10 }} />}
    {data && <>
      <section className="management-signal-strip">
        <article><img src="/management-dashboard/signal-project-scope.webp" alt="" width="192" height="192"
          loading="lazy" decoding="async" /><div><span>范围内项目</span><strong>{data.projects.length}</strong>
          <small>{data.scope.isSystemAdmin ? '全局管理范围' : '本人负责范围'}</small></div></article>
        <article className={riskCount ? 'is-risk' : ''}><img src="/management-dashboard/signal-risk-focus.webp" alt=""
          width="192" height="192" loading="lazy" decoding="async" /><div><span>需要关注</span><strong>{riskCount}</strong>
          <small>积压 ≥ 10 或交付完成率 &lt; 50%</small></div></article>
        <article><img src="/management-dashboard/signal-delivered.webp" alt="" width="192" height="192"
          loading="lazy" decoding="async" /><div><span>周期已交付</span><strong>{delivered}</strong><small>按有效交付记录</small></div></article>
        <article><img src="/management-dashboard/signal-backlog.webp" alt="" width="192" height="192"
          loading="lazy" decoding="async" /><div><span>当前积压</span><strong>{backlog}</strong><small>未启动或未进入制作</small></div></article>
      </section>
      <section className="management-data-deck"><header><div><span>{view === 'PROJECTS' ? 'PROJECT PULSE' : 'TEAM THROUGHPUT'}</span>
        <h2>{view === 'PROJECTS' ? '项目推进' : '人员工作量'}</h2></div><small><Icon name="info" size={14} />{view === 'PROJECTS'
          ? '点击项目行查看项目内人员交付，不离开当前页面。' : '混合职责分别计数；同职责同内容只计一次。'}</small></header>
        {view === 'PROJECTS' ? <Table rowKey="projectId" columns={projectColumns} dataSource={data.projects}
          pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: 1480 }}
          onRow={(record) => ({ onClick: () => setSelectedProject(record), className: 'management-clickable-row' })} />
          : <Table rowKey="personId" columns={peopleColumns} dataSource={data.people}
            pagination={{ pageSize: 30, hideOnSinglePage: true }} scroll={{ x: 1280 }}
            onRow={(record) => ({ onClick: () => setSelectedPerson(record), className: 'management-clickable-row' })} />}
      </section>
    </>}
    <Drawer size={860} open={!!selectedProject} onClose={() => setSelectedProject(null)}
      title={selectedProject ? `${selectedProject.projectName} · 项目内人员交付` : '项目内人员交付'}>
      {selectedProject && <div className="management-drilldown"><header><span><b>{selectedProject.managerName}</b><small>项目负责人</small></span>
        <span><b>{selectedProject.periodDeliveredCount}</b><small>周期交付量</small></span>
        <span><b>{selectedProject.contentDeliveryCompletionRate}%</b><small>内容交付完成率</small></span>
        <span><b>{selectedProject.packageFulfillmentRate}%</b><small>服务包履约进度</small></span></header>
        <Table rowKey="personId" size="small" pagination={false} dataSource={selectedProject.members}
          columns={[...peopleColumns.filter((column) => !['planningCount','editingCount','publicationCount','managementDeliveryCount','managedProjectCount','unclassifiedCount']
            .includes(String(column.key))), { title: '最近动作', dataIndex: 'lastActionAt', key: 'lastActionAt', width: 150,
              render: (value: string | null) => value ? new Date(value).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai',
                month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—' }]}
          scroll={{ x: 850 }} />
      </div>}
    </Drawer>
    <Drawer size={920} open={!!selectedPerson} onClose={() => setSelectedPerson(null)}
      title={selectedPerson ? `${selectedPerson.displayName} · ${periodLabel(period, range)} 事实明细` : '人效事实明细'}>
      {personDetail.isPending && <Skeleton active paragraph={{ rows: 8 }} />}
      {personDetail.isError && <Alert type="error" showIcon title="人效明细读取失败"
        description={errorText(personDetail.error)} action={<Button onClick={() => void personDetail.refetch()}>重试</Button>} />}
      {personDetail.data && <div className="management-drilldown management-person-facts">
        <header>{Object.entries(FACT_LABELS).map(([metric, label]) => <span key={metric}><b>
          {personDetail.data.facts.filter((fact) => fact.metric === metric).length}</b><small>{label}</small></span>)}</header>
        <Table rowKey={(fact) => `${fact.metric}-${fact.referenceId}`} size="small" pagination={{ pageSize: 30,
          hideOnSinglePage: true }} dataSource={personDetail.data.facts} columns={[
          { title: '类型', dataIndex: 'metric', key: 'metric', width: 110,
            render: (metric: keyof typeof FACT_LABELS) => <Tag>{FACT_LABELS[metric]}</Tag> },
          { title: '项目', dataIndex: 'projectName', key: 'projectName', width: 160 },
          { title: '内容', key: 'content', width: 220,
            render: (_: unknown, fact) => <span><b>{fact.contentTitle ?? '待定标题'}</b><br /><small>{fact.contentCode}</small></span> },
          { title: '任务 / 动作', dataIndex: 'taskName', key: 'taskName', width: 190,
            render: (value: string | null, fact) => value ?? FACT_LABELS[fact.metric] },
          { title: '业务日期', dataIndex: 'occurredOn', key: 'occurredOn', width: 110 },
        ]} scroll={{ x: 790 }} />
      </div>}
    </Drawer>
  </main>;
}
